import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAccountControllerUpdateSegmentMutation } from '@/services/generated/api';
import { useActiveTenant } from '@/features/account/use-active-tenant';
import { Button } from '@/components/ui/button';
import { CenteredCardLayout } from '@/components/ui/centered-card-layout';
import { Skeleton } from '@/components/ui/skeleton';
import { SEGMENT_OPTIONS, type TenantSegment } from '@/features/onboarding/segment';

/**
 * Screen 4 of `docs/DIGITAL_ONBOARDING.md`: one question, five answers, and it
 * genuinely changes what happens next.
 *
 * ## Why it is asked here and not at sign-up
 *
 * The study is explicit that segmentation goes *after* the account exists,
 * never before: a question in front of the form is a reason to close the tab,
 * and the same question after it is a reason to keep going, because by then
 * there is something to keep going *with*.
 *
 * ## Why by what somebody does, not by who they are
 *
 * Routing by goal beats routing by job title. Two people who both answer
 * «gerente de finanzas» can be at opposite ends of this list — one has never
 * issued a document, the other has been issuing through the SII's free system
 * for a decade and needs their history imported, not an explanation of what a
 * certificate is.
 *
 * ## Why there is no «prefiero no decirlo»
 *
 * It was offered and was removed. The answer is not demographics: it decides
 * which steps the checklist shows at all, so declining it does not save
 * somebody a question — it costs them the branch that would have removed three.
 *
 * The «Continuar» button below still records an answer of `null` when nobody
 * picked an option, and that stays deliberate: it is the shape the endpoint
 * already accepts, and the screen never asks twice.
 */
const TCIT_BRAND = <span className="text-h3 font-extrabold text-fg">TCIT</span>;

export function SegmentationPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { tenantId, isLoading } = useActiveTenant();
  const [update, { isLoading: isSaving }] = useAccountControllerUpdateSegmentMutation();
  const [chosen, setChosen] = React.useState<TenantSegment | null>(null);

  async function answer(segment: TenantSegment | null): Promise<void> {
    // The answer steers the checklist; it does not gate it. A tenant that
    // cannot be resolved, or a request that fails, must not strand somebody on
    // a question — they go on, and the checklist shows its unbranched form.
    if (tenantId !== '') {
      await update({ tenantId, updateSegmentDto: { segment } });
    }
    navigate('/get-started', { replace: true });
  }

  return (
    <CenteredCardLayout
      title="¿Cómo emites tus documentos tributarios hoy?"
      description="Con esto te mostramos sólo los pasos que te faltan a ti."
      brand={TCIT_BRAND}
    >
      {isLoading ? (
        <div className="flex flex-col gap-stack-sm">
          <Skeleton width="w-full" />
          <Skeleton width="w-full" />
          <Skeleton width="w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-stack-sm">
          {SEGMENT_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={chosen === option.value}
              onClick={() => {
                setChosen(option.value);
                void answer(option.value);
              }}
              disabled={isSaving}
              className={`flex items-center gap-inset rounded-md border p-inset text-left transition-colors focus-ring disabled:pointer-events-none disabled:border-border disabled:bg-page disabled:text-fg-muted ${
                chosen === option.value
                  ? 'border-action bg-action-subtle'
                  : 'border-border hover:border-action hover:bg-action-subtle'
              }`}
            >
              <span className="flex min-w-0 flex-1 flex-col gap-stack-sm">
                <span className="text-body font-medium text-fg">{option.label}</span>
                <span className="text-body-s text-fg-muted">{option.hint}</span>
              </span>
              <ArrowRight aria-hidden="true" className="size-icon-sm shrink-0 text-fg-muted" />
            </button>
          ))}
        </div>
      )}

      <div className="pt-stack">
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={isSaving}
          onClick={() => void answer(chosen)}
        >
          {isSaving ? 'Guardando…' : 'Continuar'}
        </Button>
      </div>
    </CenteredCardLayout>
  );
}
